import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Paper, useTheme, Typography, TextField, ThemeProvider, CssBaseline, Backdrop, CircularProgress } from "@mui/material";
import { Add, Cancel, Delete, Edit, RestartAlt, Save, Stop, Visibility } from "@mui/icons-material";
import { CREATE_POD_URL, DELETE_POD_URL, GET_ALL_PODS_URL, UPDATE_POD_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { Pod as Pod_Structure } from "../../Interfaces/AutomateTest";
import { useSelector } from "react-redux";
import { RootState } from "state/store";

export const Pod: React.FC = () => {
    const theme = useTheme();
    const { tenantId } = useParams<{ tenantId: string }>();
    const [tenantPods, setTenantPods] = useState<Pod_Structure[]>([]);
    const [editMode, setEditMode] = useState<string | null>(null);
    const [podEditedRow, setPodEditedRow] = useState<Partial<Pod_Structure>>({});
    const [newPodRow, setnewPodRow] = useState<Pod_Structure | null>(null);
    const [nameError, setNameError] = useState(false);
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const user = useSelector((state: RootState) => state.authentication.user);
 
  // Fetch Pods from the API
    const fetchPods = async () => {
      try {
        setLoading(true);
        const response = await fetch(GET_ALL_PODS_URL, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
        Logger.error("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : fetchPods(): response  " ,JSON.stringify(response, null, 2));

        if (!response.ok) {
            Logger.error("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : fetchPods(): issue while getting Pod, response is " , response.statusText);
            setLoading(false);
            throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data.data)) {
            Logger.debug("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : fetchPods():  Pods are: " , JSON.stringify(data.data, null, 2));
            setTenantPods(data.data);
            setLoading(false);
        } else {
            Logger.error("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : fetchPods(): ", "Unexpected API response format: " + JSON.stringify(data.data, null, 2));
            setTenantPods([]);
            setLoading(false);
        }
      } catch (error) {
        Logger.error("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : fetchPods(): ", "Failed to fetch pods: "+ error);
        setTenantPods([]);
        setLoading(false);
      }
    };

    useEffect(() => {
        fetchPods();
    }, []);

const handleAddNewPodRow = () => {
    const newPod: Pod_Structure = {
      _id: "",
      userId: user._id,
      companyId: user.companyId,
      name: "",
      type: "",
      apiKey: "",
      location: {},
      description: "",
      status: "",
      operatingSystem: "windows",
      supportedBrowsers: "Chrome",
      lastSeenLive: new Date,
    };
    setnewPodRow(newPod);
  };

  const handlePodEdit = (_id: string) => {
    setEditMode(_id);
    const pod = tenantPods.find((pod) => pod._id === _id);
    if (pod) setPodEditedRow(pod);
  };

  const handleCancelEdit = () => {
    // setnewDataSetRow(null);
    setnewPodRow(null);
    setEditMode(null);
    setPodEditedRow({});
  };

  const handleCreateNewPod = async () => {
    if (newPodRow) {
      try {
        if (!newPodRow.name) {
          setNameError(true);
          return;
        }
        setLoading(true);
        const response = await fetch(CREATE_POD_URL, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newPodRow),
        });

        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchPods();
        setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : handleCreateNewPod(): ", "Failed to save new Pod: "+ JSON.stringify(setTenantPods, null, 2));

        setnewPodRow(null);
      } catch (error) {
        setLoading(true);
        Logger.debug("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : handleCreateNewPod(): ", "Failed to save new Pod: "+ error);
      }
    }
  };

  const handleUpdatePod = async (_id:string) => {
    try {
      setLoading(true);
      const response = await fetch(`${UPDATE_POD_URL}/${_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(podEditedRow),
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }

      setTenantPods((prev) =>
        prev.map((pod) =>
          pod._id === podEditedRow._id ? { ...pod, ...podEditedRow } : pod
        )
      );
      setLoading(false);
      setEditMode(null);
      setPodEditedRow({});
    } catch (error) {
      setLoading(false);
      Logger.error("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : handleUpdatePod(): ", "Failed to save pod:" + error);
    }
  };

  const handlePodInputChange = (_id: string, field: keyof Pod_Structure, value: string) => {
    if (_id === "" && newPodRow) {
      setnewPodRow((prev) => prev ? { ...prev, [field]: value } : null);
      if (field === "name" && value) {
        setNameError(false);
      }
    } else if (editMode === _id) {
      setPodEditedRow((prev) => ({ ...prev, [field]: value }));
    } else {
      setTenantPods((prev) =>
        prev.map((pod) =>
          pod._id === _id ? { ...pod, [field]: value } : pod
        )
      );
    }
  };

  const handleDeletePod = async(_id: string) => {
    try {
      setLoading(true);
        const response = await fetch(DELETE_POD_URL+`/${_id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchPods();
        // setPods((prevPod) => prevPod.filter((pod) => pod._id !== _id));
      } catch (error) {
          Logger.error("st-ck-client/src/modules/AutomateTest/Pod/Pod.ts : handleDeletePod(): ", "Failed to delete pod:"+ error);
          setLoading(false);
      }
  };


  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom>
        Tenant - All Pod Pods
      </Typography>
      <Box mb={2}>
        <Button variant="contained" sx={{ backgroundColor: theme.palette.primary.main }} startIcon={<Add />} onClick={handleAddNewPodRow}>
        Register New Pod
        </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: "550px", overflow: "auto" }}>
        <Table sx={{ tableLayout: "auto", "& td, & th": { maxWidth: "250px", width:"180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>API KEY</TableCell>
              <TableCell>TYPE</TableCell>
              <TableCell>STATE</TableCell>
              <TableCell>ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenantPods.map((pod, index) => (
              <TableRow key={pod._id}>
                <TableCell>{index}</TableCell>
                <TableCell>
                  {editMode === pod._id ? (
                    <TextField size="small" label="Name"
                      value={podEditedRow.name || ""}
                      onChange={(e) => handlePodInputChange(pod._id, "name", e.target.value)} error={nameError}
                      helperText={nameError ? "Name is required" : ""}
                    />
                  ) : (
                    pod.name
                  )}
                </TableCell>
                <TableCell>{"API Key"}</TableCell>
                <TableCell>{pod.type}</TableCell>
                <TableCell>{pod.status}</TableCell>
                <TableCell>
                  {editMode === pod._id ? (
                    <>
                      <IconButton onClick={()=>handleUpdatePod(pod._id)}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelEdit}>
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => navigate(`/tenant/${tenantId}/pod/${pod._id}`)}>
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handlePodEdit(pod._id)}>
                        <Edit />
                      </IconButton>
                      <IconButton>
                        <RestartAlt />
                      </IconButton>
                      <IconButton>
                        <Stop />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePod(pod._id)}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
              {newPodRow && (
              <TableRow>
                <TableCell>New</TableCell>
                <TableCell>
                  <TextField size="small" label="Name"
                    value={newPodRow.name}
                    onChange={(e) => handlePodInputChange("", "name", e.target.value)} error={nameError}
                    helperText={nameError ? "Name is required" : ""}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" label="API Key"
                    value={newPodRow.apiKey}
                    onChange={(e) => handlePodInputChange("", "apiKey", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" label="Type"
                    value={newPodRow.type}
                    onChange={(e) => handlePodInputChange("", "type", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" label="Status"
                    value={newPodRow.status}
                    onChange={(e) => handlePodInputChange("", "status", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                <TableCell>
                  <IconButton onClick={handleCreateNewPod}>
                    <Save />
                  </IconButton>
                  <IconButton onClick={handleCancelEdit}>
                    <Cancel />
                  </IconButton>
                </TableCell>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
